import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useRepo from '@/nebula/repo';
import { computed } from 'vue';
import { doProvisioningStatus } from '@/api';
import { provisionedUser as provisionedUserConfig, userProvisioning as userProvisioningConfig } from 'shared/api/query/configs.json';
import { useStore } from 'vuex';
import { userProvisioningAttribute } from 'shared/constants.json';

export default function useUserProvisioning() {
  const store = useStore();
  const repo = useRepo(userProvisioningConfig.model);
  const provisionedUserRepo = useRepo(provisionedUserConfig.model);

  const { loggedInUserAccount } = useLoggedInUserAccount();

  const userProvisioning = computed(() => repo.selectSingle(loggedInUserAccount.value.userProvisioning?.uid));

  const isMultiple = (field) => {
    const multipleFields = [
      userProvisioningAttribute.emails,
      userProvisioningAttribute.phoneNumbers,
      userProvisioningAttribute.ims,
      userProvisioningAttribute.photos,
      userProvisioningAttribute.entitlements,
      userProvisioningAttribute.roles,
    ];

    return multipleFields.includes(field);
  };

  const isGroup = (field) => field === userProvisioningAttribute.groups;

  const getProvisioningStatus = () => doProvisioningStatus(userProvisioning.value).then((response) => {
    if (response.status !== 200) {
      throw new Error('error fetching user provisioning status');
    }

    store.commit('USER_PROVISIONING_STATUS', { provisioningStatus: response.data });

    return response;
  });

  const createSingle = (entity, options, hookParameter) => repo.createSingle(entity, options, hookParameter).then(() => {
    store.commit('USER_PROVISIONING_STATUS', { provisioningStatus: { provisionedUserCount: 0, userCount: 0, updatesAvailable: 0 } });
    repo.updateSingle({ uid: userProvisioning.value.uid, provisionedUsers: [] });
  });

  const provisionedUserSorter = (a, b) => (new Date(a.createdAt).getTime()) - (new Date(b.createdAt)).getTime();

  const findProvisionedUser = (user) => {
    const available = provisionedUserRepo.entityList.value.filter((pu) => pu.externalId === user.externalId);
    if (available.length === 0) {
      return undefined;
    }

    const active = available.filter((pu) => pu.deactivatedAt === null);
    if (active.length !== 0) {
      active.sort(provisionedUserSorter);
      return active[0];
    }

    available.sort(provisionedUserSorter);
    return available[0];
  };

  return { ...repo, createSingle, isMultiple, isGroup, userProvisioning, getProvisioningStatus, findProvisionedUser };
}
